/**
 * "Edit" on a queued reply: move it to a different time.
 *
 * Time only. The reply text, the thread and which question is being answered are untouched, which
 * is why this needs no Gmail token and does not go through the editor — a queued reply whose WORDS
 * need changing is cancelled and written again, and that stays deliberately separate.
 *
 * Prefilled with the time the reply currently has, converted back into Berlin wall-clock so the
 * field shows what the strip shows.
 */
import { useState } from 'react';
import { Modal, ModalContent, ModalHeader, ModalTitle } from '@finn-auto/design-system/atoms/modal';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import { berlinWallClockToIso } from '../rescheduleForm';
import { ModalActions } from './ModalActions';
import { ScheduleTimePicker } from './ScheduleTimePicker';
import { formatSendAt, toBerlinInputValue } from './scheduleReplyTime';
import { useScheduledSends } from './useScheduledSends';
import type { ScheduledSend } from '../../services/taScheduledSendsApi';

export function RescheduleSendModal({
  row,
  onClose,
}: {
  row: ScheduledSend;
  onClose: () => void;
}) {
  const { reschedule } = useScheduledSends();
  const [value, setValue] = useState(() => toBerlinInputValue(new Date(row.sendAt)));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setError(null);
    const sendAt = berlinWallClockToIso(value);
    if (!sendAt) {
      setError('Pick a date and time.');
      return;
    }
    setSaving(true);
    try {
      const moved = await reschedule(row, sendAt);
      showToast({ status: 'success', description: `Moved to ${formatSendAt(moved.sendAt)}.` });
      onClose();
    } catch (err) {
      // Includes SendInFlight — its message already says the reply is going out right now.
      const message = (err as Error)?.message || 'Could not move the scheduled reply.';
      setError(message);
      showToast({ status: 'error', description: message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onOpenChange={(next: boolean) => !next && onClose()}>
      <ModalContent className="!h-max !w-[min(100vw-2rem,32rem)] gap-4 rounded p-6" data-no-toggle>
        <ModalHeader>
          <ModalTitle>
            Move the reply to {row.candidateName || 'this candidate'}
          </ModalTitle>
        </ModalHeader>
        <ScheduleTimePicker value={value} onChange={setValue} disabled={saving} error={error} />
        <ModalActions
          confirmLabel={saving ? 'Moving…' : 'Move'}
          confirmDisabled={saving || !value}
          onConfirm={() => void save()}
          onCancel={onClose}
        />
      </ModalContent>
    </Modal>
  );
}
